/**
 * The Marketplace catalogue — the apps the Marketplace dashboard can show, and
 * the words that describe them.
 *
 * WRITTEN IN CODE ON PURPOSE: there is no table behind this. Changing a
 * description is a code change. The copy below is a first draft written from
 * what each integration does in the app, for review before release.
 *
 * WHICH APPS APPEAR: an app must first pass the same module gate the sidebar
 * uses for it under Admin → Marketplace (the page runs the sidebar's own
 * filter, filterNavItemsByModuleAccess). Switching a module on is a Prospera
 * team action. It must then be SET UP for the account, judged by the same
 * endpoints its setup screen reads: an app with an `integrationKey` needs at
 * least one product switched on for the practice (GET /integrations/enabled),
 * and one with `requiresOnboarding` needs its onboarding completed too. An app
 * with no `integrationKey` has nothing to set up in Prospera, so its module
 * being on is enough.
 */
import { FlaskConical, Leaf, Pill, Syringe, type LucideIcon } from 'lucide-react';
import type { NavItemDef } from '@/components/layout/navigation.config';

export type MarketplaceCategory = 'labs' | 'pharmacy' | 'supplements';

export const CATEGORY_LABELS: Record<MarketplaceCategory, string> = {
  labs: 'Lab testing',
  pharmacy: 'Pharmacy & peptides',
  supplements: 'Supplements',
};

/** The card's main button: open the app's page. Labs have none — they are ordered from the patient's chart. */
export interface MarketplaceAction {
  label: string;
  href: string;
}

export interface MarketplaceApp {
  id: string;
  name: string;
  category: MarketplaceCategory;
  /** One line for the card. */
  tagline: string;
  /** The longer explanation in the details modal. */
  description: string;
  features: string[];
  /** What to know before connecting. */
  goodToKnow: string[];
  /** The module gate — identical to the app's Admin → Marketplace sidebar entry. */
  requiresModuleActive: string;
  /** Its key on /integrations/enabled; set up once it lists a product. None: nothing to set up. */
  integrationKey?: string;
  /** Also needs /integrations/{integrationKey}/onboarding completed (Axiom Connect enrollment). */
  requiresOnboarding?: boolean;
  /** Vendor keys whose orders count as this app's, when more than `integrationKey` (Axiom's two lines). */
  orderKeys?: string[];
  /** Its Admin → Marketplace setup page — the sidebar entry whose module gate it shares. */
  setupHref: string;
  /** Shown beside the category on the card. */
  icon: LucideIcon;
  /** The card's main button, when the app has a page of its own (needs user level 2, like the pages it opens). */
  action?: MarketplaceAction;
  /** The patient-chart section (PatientDetailPage `?section=`) where its orders are. None: no orders in Prospera. */
  chartSection?: 'labs' | 'imaging' | 'peptides' | 'partell';
}

/** The Marketplace Orders page (marketplaceApps.ts re-exports it with its presets). */
export const MARKETPLACE_ORDERS_PATH = '/marketplace/orders';

/** An app with no page of its own opens the Marketplace Orders page on its orders. */
export function appOrdersAction(vendorKey: string): MarketplaceAction {
  return { label: 'Open', href: `${MARKETPLACE_ORDERS_PATH}?vendor=${encodeURIComponent(vendorKey)}` };
}

/** Shared by every lab-style vendor: they all set up through the same screen. */
const LAB_SETUP_NOTE =
  'Setup covers how the test is billed and the price your patients pay. The vendor connection itself is managed by the Prospera team.';

export const MARKETPLACE_APPS: MarketplaceApp[] = [
  {
    id: 'siphox',
    name: 'SiPhox Health',
    category: 'labs',
    tagline: 'At-home blood-test kits, ordered from the patient record.',
    description:
      'Order SiPhox Health at-home blood-test kits for your patients without leaving Prospera. The kit is sent to the patient, who collects a sample at home, and results come back to the practice.',
    features: [
      'At-home blood testing — no lab visit for the patient',
      'Ordered from the patient record',
      'Set your own price, with monthly reconciliation of what was ordered',
    ],
    goodToKnow: [LAB_SETUP_NOTE],
    requiresModuleActive: 'siphox',
    integrationKey: 'siphox',
    action: appOrdersAction('siphox'),
    setupHref: '/admin/marketplace/siphox',
    icon: FlaskConical,
    chartSection: 'labs',
  },
  {
    id: 'trudiagnostic',
    name: 'TruDiagnostic',
    category: 'labs',
    tagline: 'At-home epigenetic and biological-age tests.',
    description:
      'Offer TruDiagnostic\'s at-home epigenetic tests — including TruAge and TruHealth — to measure biological age and health markers. Order for a patient from Prospera and track it through to results.',
    features: [
      'Biological-age and epigenetic testing (TruAge, TruHealth)',
      'At-home collection',
      'Set your own price, with monthly reconciliation',
    ],
    goodToKnow: [LAB_SETUP_NOTE],
    requiresModuleActive: 'trudiagnostics',
    integrationKey: 'trudiagnostic',
    action: appOrdersAction('trudiagnostic'),
    setupHref: '/admin/marketplace/trudiagnostic',
    icon: FlaskConical,
    chartSection: 'labs',
  },
  {
    id: 'genova',
    name: 'Genova Diagnostics',
    category: 'labs',
    tagline: 'Systems-based clinical lab tests — GI, hormone, nutritional and allergy.',
    description:
      'Order Genova Diagnostics\' systems-based clinical lab tests from Prospera, covering gut health, hormones, nutrition and allergies, for a fuller picture of how the body\'s systems are working together.',
    features: [
      'GI, hormone, nutritional and allergy panels',
      'Ordered from the patient record',
      'Set your own price, with monthly reconciliation',
    ],
    goodToKnow: [LAB_SETUP_NOTE],
    requiresModuleActive: 'genova diagnostics',
    integrationKey: 'genova',
    action: appOrdersAction('genova'),
    setupHref: '/admin/marketplace/genova',
    icon: FlaskConical,
    chartSection: 'labs',
  },
  {
    id: 'bodyspec',
    name: 'BodySpec',
    category: 'labs',
    tagline: 'DEXA body-composition scans, in person or mobile.',
    description:
      'Book BodySpec DEXA scans for patients — in person or with a mobile unit — to measure body fat, lean mass and bone density accurately, and track progress between visits.',
    features: [
      'DEXA body-composition scans',
      'In-person or mobile appointments',
      'Set your own price, with monthly reconciliation',
    ],
    goodToKnow: [LAB_SETUP_NOTE],
    requiresModuleActive: 'bodyspec',
    integrationKey: 'bodyspec',
    action: appOrdersAction('bodyspec'),
    setupHref: '/admin/marketplace/bodyspec',
    icon: FlaskConical,
    // A scan, not a lab: BodySpec orders are in the chart's Scans/Other section.
    chartSection: 'imaging',
  },
  {
    id: 'axiom',
    name: 'Axiom',
    category: 'pharmacy',
    tagline: 'Peptide and Rx pens and vials, shipped to the patient.',
    description:
      'Order peptides and prescription pens — and, where enabled, peptide and GLP-1 vials, nasal sprays and supplies — through Axiom Connect, shipped straight to the patient. Staff place orders from the Peptides page.',
    features: [
      'Peptide and Rx pens; vials, nasal sprays and supplies where enabled',
      'Shipped directly to the patient',
      'Orders placed from the Peptides page',
    ],
    goodToKnow: [
      'Setup covers billing and the price your patients pay. The Axiom connection itself is managed by the Prospera team.',
    ],
    requiresModuleActive: 'axiom',
    integrationKey: 'axiom',
    requiresOnboarding: true,
    orderKeys: ['axiom', 'axiom_vials'],
    setupHref: '/admin/marketplace/axiom',
    icon: Syringe,
    action: { label: 'Open', href: '/peptides' },
    chartSection: 'peptides',
  },
  {
    id: 'partell',
    name: 'Partell Pharmacy',
    category: 'pharmacy',
    tagline: 'Send compounded and Rx prescriptions to Partell.',
    description:
      'Submit compounded and prescription medications to Partell Pharmacy from Prospera. Staff send a real prescription — drug, directions, refills, diagnosis code and prescriber details — rather than picking from a catalogue.',
    features: [
      'Compounded and prescription medications',
      'Full prescription details sent electronically',
      'A flat in-app fee per prescription you can set',
    ],
    goodToKnow: [
      'Available only to practices with an agreement with Partell, as it can include controlled substances.',
      'Prescriptions need the prescriber\'s NPI, and a DEA number where required.',
    ],
    requiresModuleActive: 'partell',
    integrationKey: 'rmm',
    setupHref: '/admin/marketplace/rmm',
    icon: Pill,
    action: { label: 'Open', href: '/partell' },
    chartSection: 'partell',
  },
  {
    id: 'vitaboom',
    name: 'Vitaboom',
    category: 'supplements',
    tagline: 'A supplement store for your patients, with protocols and dropship.',
    description:
      'Recommend and sell supplements through Vitaboom. Build supplement protocols for patients, place orders for them or let them order directly, and see the practice\'s orders and spend in one place.',
    features: [
      'Supplement protocols from the patient\'s Vitamins tab',
      'Patient and dropship orders',
      'Practice-wide view of orders and spend',
    ],
    goodToKnow: [
      'Day to day, staff use the Vitamins tab on each patient. The admin page shows orders and spend across the practice.',
    ],
    requiresModuleActive: 'vitaboom',
    setupHref: '/admin/marketplace/vitaboom',
    icon: Leaf,
    action: { label: 'Open', href: '/vitaboom' },
  },
];

/** The catalogue in the sidebar's own shape, so the sidebar's filter decides visibility. */
export function asNavItems(apps: MarketplaceApp[]): NavItemDef[] {
  return apps.map((app) => ({
    label: app.id,
    href: app.setupHref,
    requiresModuleActive: app.requiresModuleActive,
  })) as NavItemDef[];
}

/**
 * Every module behind a Marketplace app. The sidebar's Marketplace group shows
 * only when at least one is switched on (requiresAnyModuleActive), so no one
 * lands on an empty Marketplace.
 */
export const MARKETPLACE_MODULE_KEYS: string[] = MARKETPLACE_APPS.map((a) => a.requiresModuleActive);
